import Accordion from '@base/components/src/Accordion';
import AffirmScript from '@base/components/src/Scripts/AffirmScript';
import { DiscountErrorState, DiscountErrorType } from '@package/core/src/shoppingCart/types';
import useAuth from '@package/hooks/src/useAuth';
import useCheckRedirect from '@package/hooks/src/useCheckRedirect';
import useShoppingCart from '@package/hooks/src/useShoppingCart';
import config from 'config-base';
import { useCallback, useEffect, useRef, useState } from 'react';
import { affirmService, storageManager } from '../../services';
import BullseyeBucks from '../BullseyeBucks';
import CartSummaryCheckout from '../CartSummaryCheckout';
import CheckoutButton from '../CheckoutButton';
import CouponCodeForm from '../CouponCodeForm';
import GiftCardForm from '../GiftCardForm';
import PayPalButton from '../PayPalButton';
import styles from './CartSummaryDetails.module.css';
import { CartSummaryDetailsProps } from './types';
import useGiftCard from '@package/hooks/src/useGiftCard';
import { GiftCard } from '@package/core/src/giftCard/types';
import SezzleWidget from '../SezzleWidget';
import PaymentMethodsDisplay from '@base/components/src/PaymentMethodsDisplay';
import { PaymentMethodsType } from '@package/core/src/payment/types';

const CartSummaryDetails = ({
  summary,
  showAffirm,
  showSezzle,
  showPayPal,
  paymentMethods,
}: CartSummaryDetailsProps) => {
  const [discountCouponCodeError, setDiscountCouponCodeError] = useState<DiscountErrorState | undefined>(undefined);
  const [giftCardError, setGiftCardError] = useState<DiscountErrorState | undefined>(undefined);
  const accordionRef = useRef<HTMLDivElement>();
  const { data: giftCardData, deleteGiftCard } = useGiftCard();
  const { cartData } = useShoppingCart();
  const { user, userLoading } = useAuth();
  const { redirectStart } = useCheckRedirect();
  const isUserAuthorized = !!user && !userLoading;

  const couponCodeErrorHandler = useCallback(async () => {
    const message = await storageManager.get('discountErrorMessage');
    if (message.data) {
      void scrollToAccordion();
      setDiscountError(JSON.parse(message.data) as unknown as DiscountErrorState);
    }
    await storageManager.delete('discountErrorMessage');
  }, []);

  const scrollToAccordion = useCallback(async () => {
    if (accordionRef.current && typeof window !== 'undefined') {
      const scrollTop: number = accordionRef.current.offsetTop;
      accordionRef.current.click();
      if (accordionRef.current) {
        setTimeout(() => {
          window.scrollTo({
            behavior: 'smooth',
            top: scrollTop,
          });
        }, 300);
      }
    }
  }, []);

  const setDiscountError = (discountError: DiscountErrorState) => {
    if (discountError.code === DiscountErrorType.CouponError) {
      setDiscountCouponCodeError(discountError);
    }
    if (discountError.code === DiscountErrorType.GiftCardError) {
      setGiftCardError(discountError);
    }
  };

  const removeDiscountError = (discountErrorTypeCode: DiscountErrorType) => {
    if (discountErrorTypeCode === DiscountErrorType.CouponError) {
      setDiscountCouponCodeError(undefined);
    }
    if (discountErrorTypeCode === DiscountErrorType.GiftCardError) {
      setGiftCardError(undefined);
    }
  };

  useEffect(() => {
    void couponCodeErrorHandler();
  }, []);

  // Refresh Affirm HTML reference
  useEffect(() => {
    affirmService.refreshAffirmUi();
  }, [summary, showAffirm]);

  return (
    <div id="cart-summary-details" className={styles.cartSummaryDetails}>
      <div className={'orderSummaryBlock cartSummaryCheckoutWithHeader'}>
        <p className={`${styles.cartSummaryCheckoutHeader} ${styles.orderSummaryLabel}`}>Order Summary</p>
        <CartSummaryCheckout
          notDeliverable={cartData?.notDeliverable}
          summary={summary}
          isUserAuthorized={isUserAuthorized}
          redirectStart={redirectStart}
          showPayPal={showPayPal}
        />
      </div>

      <div className={styles.cartSummaryControllers}>
        {summary ? (
          <>
            <CheckoutButton
              id="cart-summary-details-proceed-btn"
              checkoutButtonAvailable={summary.checkoutButtonAvailable || ''}
              redirectStart={redirectStart}
              notDeliverable={Boolean(cartData?.notDeliverable)}
              isUserAuthorized={isUserAuthorized}
            />
            {showPayPal && (
              <PayPalButton
                hide={Boolean(summary.checkoutButtonAvailable) || !summary.totalDue}
                notDeliverable={Boolean(cartData?.notDeliverable)}
                label="checkout"
                page="shopping-cart"
                fullWidth
              />
            )}
            {paymentMethods ? (
              <PaymentMethodsDisplay
                paymentMethods={paymentMethods}
                ignore={[PaymentMethodsType.Affirm, PaymentMethodsType.Sezzle]}
                className={styles.paymentMethodsDisplay}
              />
            ) : null}
            {showAffirm && (
              <>
                <div className={styles.affirmContainer}>
                  <div id="more-information" className={styles.affirmMoreInfo}>
                    <p
                      className="affirm-as-low-as"
                      data-page-type="category"
                      data-amount={summary.totalDue.toFixed(2).toString().replace(/\D/g, '')}
                    ></p>
                  </div>
                </div>
                <AffirmScript publicApiKey={config.affirmPublicApiKey} scriptUrl={config.affirmScriptUrl} />
              </>
            )}

            {showSezzle && <SezzleWidget salePrice={summary.totalDue.toFixed(2).toString()} showSezzle={showSezzle} />}
          </>
        ) : null}

        <BullseyeBucks
          giftCards={giftCardData?.filter((giftCard: GiftCard) => giftCard.loyaltyCard)}
          onDelete={deleteGiftCard}
        />

        <Accordion
          data-testid="coupon-and-gift-card"
          componentRef={accordionRef}
          title={'Apply Coupon Code or Gift Card'}
          headerClassName={styles.accordionHeader}
          className={styles.cartSummaryAccordion}
          contentClassName={styles.cartSummaryAccordionContent}
          iconClassName={styles.cartSummaryAccordionIcon}
        >
          <CouponCodeForm
            id="coupon-form"
            buttonLabel={'Apply'}
            couponCodeError={cartData?.applyCouponMessage}
            discountCouponError={discountCouponCodeError}
            removeCouponCodeError={removeDiscountError}
          />

          <div className={styles.giftCardFormContainer}>
            <GiftCardForm
              id="gift-card-form"
              fullWidth
              buttonLabel={'Apply'}
              giftCardError={giftCardError}
              giftCards={giftCardData?.filter((giftCard: GiftCard) => !giftCard.loyaltyCard)}
              removeGiftCardError={removeDiscountError}
              onDelete={deleteGiftCard}
            />
          </div>
        </Accordion>
      </div>
    </div>
  );
};

export default CartSummaryDetails;
